import type { PowerupKind } from "../../game/types";
import { RADIAL_RADIUS_PX, RADIAL_ITEM_SIZE_PX, type RadialMenuState } from "../../game/radialMenu";
import { mFont } from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface RadialMenuDeps {
  nowMs: () => number;
  powerupIconImages: Partial<Record<PowerupKind, HTMLImageElement>>;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawRadialMenuScreen(
  deps: RadialMenuDeps,
  ctx: CanvasRenderingContext2D,
  menu: RadialMenuState,
): void {
  const progress = Math.max(0, Math.min(1, menu.openProgress));
  if (progress <= 0) return;

  ctx.save();
  ctx.globalAlpha = progress;

  const hubX = menu.hubX;
  const hubY = menu.hubY;
  const radius = RADIAL_RADIUS_PX * progress;
  const itemSize = RADIAL_ITEM_SIZE_PX;

  // Draw each item
  for (let i = 0; i < menu.items.length; i++) {
    const item = menu.items[i];
    const ix = hubX + Math.cos(item.angle) * radius;
    const iy = hubY + Math.sin(item.angle) * radius;
    const isHighlighted = menu.highlightedIndex === i;
    const halfSize = itemSize / 2;

    // Item circle background
    ctx.beginPath();
    ctx.arc(ix, iy, halfSize, 0, Math.PI * 2);

    if (!item.enabled) {
      ctx.fillStyle = "rgba(16,18,26,0.92)";
    } else if (isHighlighted) {
      const hue = (deps.nowMs() / 5) % 360;
      ctx.fillStyle = `hsla(${hue.toFixed(0)},70%,20%,0.95)`;
    } else {
      ctx.fillStyle = "rgba(22,40,66,0.92)";
    }
    ctx.fill();

    // Item border
    ctx.beginPath();
    ctx.arc(ix, iy, halfSize, 0, Math.PI * 2);
    if (isHighlighted && item.enabled) {
      ctx.save();
      const hue = (deps.nowMs() / 4) % 360;
      ctx.shadowColor = `hsla(${hue.toFixed(0)},100%,65%,0.8)`;
      ctx.shadowBlur = 12;
      ctx.strokeStyle = `hsla(${hue.toFixed(0)},100%,70%,1)`;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      ctx.restore();

      // Connector line from behind the skin to highlighted item
      const lineAngle = Math.atan2(iy - hubY, ix - hubX);
      const skinRadius = 40;
      const lineStartX = hubX + Math.cos(lineAngle) * skinRadius;
      const lineStartY = hubY + Math.sin(lineAngle) * skinRadius;
      ctx.beginPath();
      ctx.moveTo(lineStartX, lineStartY);
      ctx.lineTo(ix, iy);
      ctx.strokeStyle = `hsla(${hue.toFixed(0)},80%,60%,0.5)`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    } else {
      ctx.strokeStyle = item.enabled ? "rgba(107,147,201,0.7)" : "rgba(50,56,72,0.5)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    ctx.lineWidth = 1;

    // Item icon (use powerup icon images if available, otherwise text label)
    const iconImage = menu.kind === "powerup" ? deps.powerupIconImages[item.id as PowerupKind] : undefined;
    if (iconImage != null) {
      const imgSize = 24;
      ctx.save();
      ctx.imageSmoothingEnabled = false;
      if (!item.enabled) ctx.globalAlpha = 0.25;
      ctx.drawImage(iconImage, ix - imgSize / 2, iy - imgSize / 2, imgSize, imgSize);
      ctx.restore();
    } else {
      ctx.fillStyle = item.enabled ? (isHighlighted ? "#fff" : "#c8dbf0") : "#4a5568";
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(item.label, ix, iy);
    }

    // Cost label hidden — icons are self-explanatory
  }

  ctx.restore();
}
